import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { getCountryFlag } from 'utils/countryFlags';
import { Checkbox } from 'components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { login, getCurrentUser, forgotPassword, ForgotPasswordRequest, register as registerUser } from 'services/authService';
import useAuthStore from 'stores/authStore';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from 'services/api';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, "Password is required"),
});

export type LoginData = z.infer<typeof loginSchema>;

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email"),
});

export type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;

const registerSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Please enter a valid email"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one digit"),
  password_confirm: z.string().min(1, "Please confirm your password"),
  demo_request: z.boolean().default(false),
  country: z.string().optional(),
  sector: z.string().optional(),
}).refine((data) => data.password === data.password_confirm, {
  message: "Passwords do not match",
  path: ["password_confirm"],
}).refine((data) => !data.demo_request || (data.demo_request && data.country && data.sector), {
  message: "Country and sector are required when requesting a demo",
  path: ["demo_request"],
});

export type RegisterData = z.infer<typeof registerSchema>;

// Slides data structure - will be populated from API later
interface Slide {
  id: number;
  imageUrl: string;
  title: string;
  description: string;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
  });
  const { register: registerForgotPassword, handleSubmit: handleForgotPasswordSubmit, formState: { errors: forgotPasswordErrors } } = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
  });
  const { register: registerRegister, handleSubmit: handleRegisterSubmit, formState: { errors: registerErrors }, watch, setValue } = useForm<RegisterData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      demo_request: false,
    },
  });
  
  const demoRequest = watch('demo_request');
  const { setUser, token } = useAuthStore();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState<string>('');
  const [registerMessage, setRegisterMessage] = useState<string>('');
  const [loginMessage, setLoginMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [demoOptions, setDemoOptions] = useState<{ countries: string[]; sectors: string[] }>({ countries: [], sectors: [] });

  // Use static slides instead of fetching from API to avoid backend issues
  useEffect(() => {
    const staticSlides: Slide[] = [
      {
        id: 1,
        imageUrl: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
        title: t('slide1.title', 'Enterprise AI Solutions'),
        description: t('slide1.description', 'Transform your business with our cutting-edge AI platform.')
      },
      {
        id: 2,
        imageUrl: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
        title: t('slide2.title', 'Advanced Document Processing'),
        description: t('slide2.description', 'Upload, categorize, and analyze documents with AI-powered tools.')
      },
      {
        id: 3,
        imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
        title: t('slide3.title', 'Smart Model Training'),
        description: t('slide3.description', 'Train custom AI models with your data securely.')
      }
    ];
    setSlides(staticSlides);
  }, [t]);

  // Fetch demo options from API
  useEffect(() => {
    const fetchDemoOptions = async () => {
      try {
        const response = await api.get('/auth/demo-options');
        setDemoOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch demo options', error);
        // Fallback to static options if API fails - using full lists from backend
        setDemoOptions({
          countries: [
            "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia",
            "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
            "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
            "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad",
            "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
            "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
            "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany",
            "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary",
            "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan",
            "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos",
            "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar",
            "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico",
            "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
            "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway",
            "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines",
            "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
            "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal",
            "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
            "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
            "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia",
            "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
            "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
          ],
          sectors: [
            "Academic/Education",
            "Healthcare/Medical",
            "Finance/Banking",
            "Technology/IT",
            "Manufacturing",
            "Retail/E-commerce",
            "Energy/Utilities",
            "Telecommunications",
            "Transportation/Logistics",
            "Government/Public Sector",
            "Legal Services",
            "Consulting",
            "Marketing/Advertising",
            "Media/Entertainment",
            "Real Estate",
            "Insurance",
            "Pharmaceuticals",
            "Biotechnology",
            "Agriculture",
            "Construction",
            "Hospitality/Tourism",
            "Non-profit/Charity",
            "Research & Development",
            "Automotive",
            "Aerospace",
            "Chemical Industry",
            "Mining",
            "Oil & Gas",
            "Environmental Services",
            "Security Services"
          ]
        });
      }
    };
    
    fetchDemoOptions();
  }, []);

  // Auto-rotate slides
  useEffect(() => {
    if (slides.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [slides.length]);

  const handleSlideChange = (index: number) => {
    setCurrentSlideIndex(index);
  };

  const onSubmit = async (data: LoginData) => {
    try {
      const tokenResponse = await login(data);
      localStorage.setItem('token', tokenResponse.access_token);
      
      // Fetch actual user data
      try {
        const userData = await getCurrentUser();
        setUser(userData, tokenResponse.access_token);
      } catch (userError) {
        console.error('Failed to fetch user data:', userError);
        // If fetching fails, decode role from token as fallback
        const tokenParts = tokenResponse.access_token.split('.');
        if (tokenParts.length === 3) {
          try {
            const payload = JSON.parse(atob(tokenParts[1]));
            const fallbackUser = {
              id: payload.sub || 'unknown',
              name: 'User',
              email: data.email,
              role: (payload.role || 'contributor') as 'admin' | 'super_admin' | 'researcher' | 'contributor',
              status: 'active' as const,
            };
            setUser(fallbackUser, tokenResponse.access_token);
          } catch {
            // If all else fails, use contributor role
            const fallbackUser = {
              id: 'unknown',
              name: 'User',
              email: data.email,
              role: 'contributor' as const,
              status: 'active' as const,
            };
            setUser(fallbackUser, tokenResponse.access_token);
          }
        }
      }
      
      navigate('/');
    } catch (error: any) {
      console.error('Login failed', error);
      
      // Show specific error messages to user
      if (error.response?.status === 401) {
        setLoginMessage(t('login.error.incorrectCredentials', 'Incorrect email or password. Please try again.'));
      } else if (error.response?.status === 400) {
        setLoginMessage(t('login.error.accountNotActive', 'Your account is not active. Please contact an administrator.'));
      } else if (error.response?.data?.detail) {
        setLoginMessage(error.response.data.detail);
      } else if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        setLoginMessage(t('login.error.network', 'Network error. Please check your connection and try again.'));
      } else {
        setLoginMessage(t('login.error.general', 'Login failed. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onForgotPasswordSubmit = async (data: ForgotPasswordData) => {
    setIsSubmitting(true);
    setForgotPasswordMessage('');
    try {
      const response = await forgotPassword(data);
      // Backend returns generic message for security (even for non-existent users)
      setForgotPasswordMessage(response.message || t('forgotPassword.message.success', 'If this email exists in our system, a password reset link has been sent.'));
    } catch (error: any) {
      console.error('Forgot password error:', error);
      setForgotPasswordMessage(error.response?.data?.detail || t('forgotPassword.message.error', 'Failed to send reset link. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRegisterSubmit = async (data: RegisterData) => {
    setIsSubmitting(true);
    setRegisterMessage('');
    try {
      await registerUser(data);
      // Show success modal instead of inline message
      setShowRegistrationSuccess(true);
    } catch (error: any) {
      console.error('Registration error:', error);
      // Show detailed validation errors if available
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          setRegisterMessage(error.response.data.detail);
        } else if (Array.isArray(error.response.data.detail)) {
          // Handle array of validation errors
          const errorMessages = error.response.data.detail.map((err: any) => 
            `${err.loc?.join('.')}: ${err.msg}`
          ).join(', ');
          setRegisterMessage(errorMessages);
        } else {
          setRegisterMessage(t('register.error.general', 'Registration failed. Please try again.'));
        }
      } else {
        setRegisterMessage(t('register.error.general', 'Registration failed. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newsletterEmail) {
      setNewsletterMessage({ type: 'error', text: t('newsletter.emailRequired', 'Please enter your email address') });
      return;
    }

    setNewsletterLoading(true);
    setNewsletterMessage(null);

    try {
      await api.post('/newsletter/subscribe', { email: newsletterEmail });
      setNewsletterMessage({ 
        type: 'success', 
        text: t('newsletter.subscriptionSuccess', 'Thank you for subscribing to our newsletter!') 
      });
      setNewsletterEmail('');
    } catch (error: any) {
      if (error.response?.status === 400) {
        setNewsletterMessage({ 
          type: 'error', 
          text: t('newsletter.alreadySubscribed', 'This email is already subscribed to our newsletter') 
        });
      } else {
        setNewsletterMessage({ 
          type: 'error', 
          text: t('newsletter.subscriptionError', 'Failed to subscribe. Please try again later.') 
        });
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  // Filter countries based on search
  const filteredCountries = demoOptions.countries.filter(country =>
    country.toLowerCase().includes(countrySearch.toLowerCase())
  );

  return (
    <div className="flex h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Left side - Slides */}
      <div className="w-1/2 flex flex-col hidden md:flex">
        {slides.length > 0 && (
          <>
            <div 
              className="flex-1 bg-cover bg-center transition-opacity duration-1000"
              style={{ backgroundImage: `url(${slides[currentSlideIndex].imageUrl})` }}
            >
              <div className="h-full bg-black bg-opacity-50 flex items-center justify-center">
                <div className="text-center text-white p-8 max-w-lg">
                  <h2 className="text-3xl font-bold mb-4">{slides[currentSlideIndex].title}</h2>
                  <p className="text-lg">{slides[currentSlideIndex].description}</p>
                </div>
              </div>
            </div>
            {/* Slide indicators */}
            <div className="p-6 flex justify-center">
              <div className="flex space-x-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleSlideChange(index)}
                    className={`w-3 h-3 rounded-full ${
                      index === currentSlideIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right side - Login form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-6 bg-white">
        <div className="w-full max-w-md space-y-4">
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <img 
                src="/fullLogoLogin.svg" 
                alt="CoGenAI Logo" 
                className="w-full max-w-xs h-auto mx-auto"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">
              {isRegistering 
                ? t('register.title', 'Create Account') 
                : isForgotPassword 
                  ? t('forgotPassword.title', 'Reset Password') 
                  : t('login.title', 'Welcome Back')}
            </h1>
            <p className="text-gray-600 mt-2">
              {isRegistering 
                ? t('register.subtitle', 'Create your account to get started') 
                : isForgotPassword 
                  ? t('forgotPassword.subtitle', 'Enter your email to reset your password') 
                  : t('login.subtitle', 'Sign in to your account')}
            </p>
          </div>

          {isRegistering ? (
            <form onSubmit={handleRegisterSubmit(onRegisterSubmit)} className="space-y-6">
              <div>
                <Label htmlFor="register-full-name">{t('register.form.name', 'Full Name')}</Label>
                <Input 
                  id="register-full-name" 
                  type="text" 
                  placeholder={t('register.form.namePlaceholder', 'Enter your full name')} 
                  autoComplete="name"
                  {...registerRegister('name')}
                />
                {registerErrors.name && (
                  <p className="text-red-500 text-sm mt-1">{registerErrors.name.message}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="register-email">{t('register.form.email', 'Email')}</Label>
                <Input 
                  id="register-email" 
                  type="email" 
                  placeholder={t('register.form.emailPlaceholder', 'Enter your email')} 
                  autoComplete="email"
                  {...registerRegister('email')}
                />
                {registerErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{registerErrors.email.message}</p>
                )}
              </div>
              
              <div className="relative">
                <Label htmlFor="register-password">{t('register.form.password', 'Password')}</Label>
                <Input 
                  id="register-password" 
                  type={showPassword ? "text" : "password"} 
                  placeholder={t('register.form.passwordPlaceholder', 'Enter your password')} 
                  autoComplete="new-password"
                  {...registerRegister('password')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-8 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                {registerErrors.password && (
                  <p className="text-red-500 text-sm mt-1">{registerErrors.password.message}</p>
                )}
              </div>
              
              <div className="relative">
                <Label htmlFor="register-password-confirm">{t('register.form.passwordConfirm', 'Confirm Password')}</Label>
                <Input 
                  id="register-password-confirm" 
                  type={showPassword ? "text" : "password"} 
                  placeholder={t('register.form.passwordConfirmPlaceholder', 'Confirm your password')} 
                  autoComplete="new-password"
                  {...registerRegister('password_confirm')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-8 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                {registerErrors.password_confirm && (
                  <p className="text-red-500 text-sm mt-1">{registerErrors.password_confirm.message}</p>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="demo_request" 
                  checked={demoRequest}
                  onCheckedChange={(checked) => {
                    setValue('demo_request', checked === true);
                  }}
                />
                <Label htmlFor="demo_request" className="text-sm font-medium">
                  Request a Demo
                </Label>
              </div>
              {registerErrors.demo_request && (
                <p className="text-red-500 text-sm">{registerErrors.demo_request.message}</p>
              )}
              
              {demoRequest && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                     <div>
                       <Label htmlFor="country" className="text-sm">Country</Label>
                       <Select onValueChange={(value) => setValue('country', value)}>
                         <SelectTrigger>
                           <SelectValue placeholder="Select your country" />
                         </SelectTrigger>
                         <SelectContent className="max-h-60 overflow-y-auto">
                           {/* Search bar */}
                           <div className="p-2 border-b">
                             <Input
                               placeholder="Search countries..."
                               value={countrySearch}
                               onChange={(e) => setCountrySearch(e.target.value)}
                               className="w-full"
                               onClick={(e) => e.stopPropagation()}
                             />
                           </div>
                           
                           {/* Country list */}
                           {filteredCountries.length > 0 ? (
                             filteredCountries.map((country) => (
                               <SelectItem key={country} value={country}>
                                 <span>{country}</span>
                               </SelectItem>
                             ))
                           ) : (
                             <div className="p-2 text-center text-gray-500">
                               No countries found
                             </div>
                           )}
                         </SelectContent>
                       </Select>
                       {registerErrors.country && <p className="text-red-500 text-sm">{registerErrors.country.message}</p>}
                     </div>
                     
                     <div>
                       <Label htmlFor="sector" className="text-sm">Sector of Activity</Label>
                       <Select onValueChange={(value) => setValue('sector', value)}>
                         <SelectTrigger>
                           <SelectValue placeholder="Select your sector" />
                         </SelectTrigger>
                         <SelectContent className="max-h-60 overflow-y-auto">
                           {demoOptions.sectors.map((sector) => (
                             <SelectItem key={sector} value={sector}>
                               {sector}
                             </SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                       {registerErrors.sector && <p className="text-red-500 text-sm">{registerErrors.sector.message}</p>}
                     </div>
                  </div>
                </>
              )}
              
              {registerMessage && (
                <div className={`text-center text-sm ${
                  registerMessage.includes('successful') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {registerMessage}
                </div>
              )}
              
              <div className="flex space-x-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setIsRegistering(false);
                    setRegisterMessage('');
                    setLoginMessage('');
                  }}
                >
                  {t('register.backToLogin', 'Back to Login')}
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('register.submitting', 'Creating Account...') : t('register.submit', 'Create Account')}
                </Button>
              </div>
            </form>
          ) : isForgotPassword ? (
            <div className="space-y-6">
              <form onSubmit={handleForgotPasswordSubmit(onForgotPasswordSubmit)} className="space-y-6">
                <div>
                  <Label htmlFor="reset-email">{t('forgotPassword.form.email', 'Email')}</Label>
                  <Input 
                    id="reset-email" 
                    type="email" 
                    placeholder={t('forgotPassword.form.emailPlaceholder', 'Enter your email')} 
                    {...registerForgotPassword('email')}
                  />
                  {forgotPasswordErrors.email && (
                    <p className="text-red-500 text-sm mt-1">{forgotPasswordErrors.email.message}</p>
                  )}
                </div>
                
                {forgotPasswordMessage && (
                  <div className={`text-center text-sm ${
                    forgotPasswordMessage.includes('sent') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {forgotPasswordMessage}
                  </div>
                )}
                
                <div className="flex space-x-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setIsForgotPassword(false);
                      setForgotPasswordMessage('');
                      setLoginMessage('');
                    }}
                  >
                    {t('forgotPassword.backToLogin', 'Back to Login')}
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t('forgotPassword.submitting', 'Sending...') : t('forgotPassword.submit', 'Send Reset Link')}
                  </Button>
                </div>
                <p className="text-center text-sm text-gray-600">
                  {t('forgotPassword.instructions', "We'll send you a link to reset your password")}
                </p>
              </form>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <Label htmlFor="email">{t('login.form.email', 'Email')}</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder={t('login.form.emailPlaceholder', 'Enter your email')} 
                  {...register('email')} 
                />
                {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
              </div>
              <div className="relative">
                <Label htmlFor="password">{t('login.form.password', 'Password')}</Label>
                <Input 
                  id="password" 
                  type={showPassword ? "text" : "password"} 
                  placeholder={t('login.form.passwordPlaceholder', 'Enter your password')} 
                  {...register('password')} 
                />
                <button
                  type="button"
                  className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                {errors.password && <p className="text-red-500 text-sm">{errors.password.message}</p>}
              </div>

              {/* Login error message */}
              {loginMessage && (
                <div className={`p-3 rounded-md text-sm ${
                  loginMessage.includes('successful') ? 'bg-green-100 text-green-800 border border-green-200' : 
                  'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {loginMessage}
                </div>
              )}

              <div className="text-right">
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:underline"
                  onClick={() => setIsForgotPassword(true)}
                >
                  {t('login.forgotPassword', 'Forgot your password?')}
                </button>
              </div>
              <Button type="submit" className="w-full">
                {t('login.submit', 'Sign In')}
              </Button>
            </form>
          )}
          {!isRegistering && !isForgotPassword && (
            <p className="text-center text-sm text-gray-600">
              {t('login.noAccount', "Don't have an account?")}{' '}
              <button
                type="button"
                className="font-medium text-blue-600 hover:underline"
                onClick={() => setIsRegistering(true)}
              >
                {t('login.signUp', 'Sign up')}
              </button>
            </p>
          )}
          {(isRegistering || isForgotPassword) && (
            <p className="text-center text-sm text-gray-600">
              {t('login.haveAccount', "Already have an account?")}{' '}
              <button
                type="button"
                className="font-medium text-blue-600 hover:underline"
                onClick={() => {
                  setIsRegistering(false);
                  setIsForgotPassword(false);
                  setRegisterMessage('');
                  setForgotPasswordMessage('');
                  setLoginMessage('');
                }}
              >
                {t('login.signIn', 'Sign in')}
              </button>
            </p>
          )}

          {/* Newsletter Subscription - Only show on login page */}
          {!isRegistering && !isForgotPassword && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center justify-center">
                  <Send className="mr-2 h-5 w-5" />
                  {t('newsletter.title', 'Stay Updated')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('newsletter.description', 'Get informed when the platform is ready')}
                </p>
              </div>
              <form onSubmit={handleNewsletterSubscribe} className="space-y-3">
                <div className="flex space-x-2">
                  <Input
                    type="email"
                    placeholder={t('newsletter.emailPlaceholder', 'Enter your email')}
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={newsletterLoading}
                    className="flex-1"
                    required
                  />
                  <Button 
                    type="submit" 
                    size="sm"
                    disabled={newsletterLoading}
                    className="whitespace-nowrap"
                  >
                    {newsletterLoading ? (
                      <>
                        <div className="mr-1 h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        {t('newsletter.subscribing', '...')}
                      </>
                    ) : (
                      <>
                        <Send className="mr-1 h-3 w-3" />
                        {t('newsletter.subscribe', 'Subscribe')}
                      </>
                    )}
                  </Button>
                </div>
                
                {newsletterMessage && (
                  <div className={`flex items-center justify-center space-x-1 text-xs ${
                    newsletterMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {newsletterMessage.type === 'success' ? (
                      <CheckCircle className="h-3 w-3" />
                    ) : (
                      <AlertCircle className="h-3 w-3" />
                    )}
                    <span>{newsletterMessage.text}</span>
                  </div>
                )}
              </form>
            </div>
          )}

          <div className="pt-8 border-t border-gray-100">
            <p className="text-center text-xs text-gray-400">
              © {new Date().getFullYear()} CoGenAI Platform. {t('login.copyright', 'All rights reserved.')}
            </p>
          </div>
        </div>
      </div>

      {/* Registration Success Modal */}
      {showRegistrationSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('register.success.title', 'Registration Successful!')}</h3>
              <p className="text-gray-600 mb-6">
                {t('register.success.message', 'Your account has been created successfully and is pending admin approval. You will receive an email notification once your account is activated.')}
              </p>
              <button
                onClick={() => {
                  setShowRegistrationSuccess(false);
                  setIsRegistering(false);
                }}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
              >
                {t('register.success.continue', 'Continue to Login')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;